import { useLocation, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Search, ChevronRight, Wifi, WifiOff } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import RoleImpersonationDropdown from './RoleImpersonationDropdown';
import NotificationBell from './NotificationBell';

const ROUTE_LABELS = {
  '': 'Dashboard',
  modules: 'Modules',
  certificates: 'Certificates',
  profile: 'Profile',
  trainer: 'Teach',
  editor: 'Editor',
  admin: 'Admin',
  analytics: 'Analytics',
  enrollments: 'Enrollments',
  users: 'Users & Access',
  org: 'Organization',
  organizations: 'Organizations',
  branding: 'Branding & Preferences',
  storage: 'Storage & Files',
  logs: 'Activity Logs',
  notifications: 'Notifications',
  maintenance: 'Maintenance',
  settings: 'Settings',
  learn: 'Learn',
  quizzes: 'Quizzes',
  upload: 'Upload',
  verify: 'Verify',
};

/* Segments that exist purely as URL groupings — they are NOT routes themselves
 * and should be rendered as plain text rather than clickable links to avoid
 * "Page Not Found" when the user clicks them. */
const NON_ROUTE_SEGMENTS = new Set(['editor', 'admin']);

const titleCase = (s) =>
  s.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function Topbar() {
  const { user, logout, impersonatingRole } = useAuth();
  const location = useLocation();
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const segments = location.pathname.split('/').filter(Boolean);
  const crumbs = segments.map((seg, i) => ({
    raw: seg,
    label: ROUTE_LABELS[seg] || titleCase(seg),
    path: '/' + segments.slice(0, i + 1).join('/'),
    linkable: !NON_ROUTE_SEGMENTS.has(seg),
  }));

  return (
    <header className={`h-[var(--topbar-height)] border-b flex items-center px-6 gap-4 shrink-0 transition-colors sticky top-0 z-20 ${impersonatingRole ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-300' : 'glass-light'}`} data-testid="topbar">
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1 text-sm flex-1 min-w-0" data-testid="breadcrumbs">
        <Link to="/" className="text-muted-foreground hover:text-foreground">Dashboard</Link>
        {crumbs.map((c) => (
          <span key={c.path} className="flex items-center gap-1">
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />
            {c.linkable ? (
              <Link to={c.path} className="text-muted-foreground hover:text-foreground truncate max-w-[160px]">{c.label}</Link>
            ) : (
              <span className="text-muted-foreground/70 truncate max-w-[160px] cursor-default">{c.label}</span>
            )}
          </span>
        ))}
      </nav>

      {/* Search */}
      <div className="relative hidden md:flex items-center">
        <Search className="absolute left-3 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search..."
          className="h-9 w-56 rounded-md border bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid="global-search"
        />
      </div>

      {/* Online/Offline indicator */}
      {!isOnline && (
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium" data-testid="offline-indicator">
          <WifiOff className="w-3.5 h-3.5" /> Offline
        </div>
      )}

      {/* Role Impersonation */}
      <RoleImpersonationDropdown />

      {/* Notifications */}
      <NotificationBell />

      {/* User dropdown */}
      {user && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 p-1 rounded-full hover:bg-muted/60 transition" data-testid="user-menu-trigger">
              <div className="w-8 h-8 rounded-full bg-gold-100 text-gold-800 flex items-center justify-center text-xs font-semibold ring-1 ring-gold/40">
                {(user.first_name?.[0] || '') + (user.last_name?.[0] || '')}
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user.first_name} {user.last_name}</p>
              <p className="text-xs text-muted-foreground">{user.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild><Link to="/profile">Profile</Link></DropdownMenuItem>
            <DropdownMenuItem asChild><Link to="/preferences" data-testid="dropdown-preferences">My Preferences</Link></DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="dropdown-logout">Log out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </header>
  );
}
